import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { LiveCallEngine, CallBar, blobToBase64, micErrorText } from './LiveCall';

/**
 * ☎️ CALL CENTER — loyiha mijozlariga javob beradigan ovozli AI operator (Boshqaruvchi F6, 2026-09-27).
 * Backend: routes/callcenter.js, services/callCenter.js (docs/knowledge/152). Eski statik maket
 * (ElevenLabs rejasi) o'rnida — endi Boshqaruvchi steki (OpenRouter, Gemini nutq/ovoz).
 *
 * Bo'limlar: 📊 Umumiy · 💬 Suhbatlar (transkript + AI xulosasi, 🔧 vazifa) · 🤖 Agent (prompt,
 * bilimlar, ovoz — faqat loyiha egasi saqlaydi) · 📚 Savol-javoblar (baza, .docx import, ❓ javobsiz savollar) ·
 * ✈️ Telegram (loyiha boti — mijozlar yozadi, agent javob beradi; docs/knowledge/170) · 🧪 Sinov · ☎️ Telefon.
 * Jonli ovoz `cc:*` socket voqealari bilan (faqat shu foydalanuvchiga). `authFetch` — ref orqali.
 */
const fmtUsd = (n) => `$${(Number(n) || 0).toFixed((Number(n) || 0) < 1 ? 3 : 2)}`;
const fmtDur = (s) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
const fmtAt = (ms) => { const s = Math.floor((ms || 0) / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const fmtTime = (v) => { const d = new Date(v); if (isNaN(d)) return ''; const p = (n) => String(n).padStart(2, '0'); return `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`; };
const FIELD_LABEL = { ism: 'Ism', telefon: 'Telefon', buyurtma: 'Buyurtma', manzil: 'Manzil', mavzu: 'Mavzu', izoh: 'Izoh' };
const card = 'bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4';
const inp = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2';

function okChip(ok) {
  if (ok == null) return <span className="text-[11px] bg-gray-100 dark:bg-gray-700 text-gray-500 rounded-full px-2 py-0.5 whitespace-nowrap">— tahlil yo'q</span>;
  return ok
    ? <span className="text-[11px] bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-full px-2 py-0.5 whitespace-nowrap">✅ Hal bo'ldi</span>
    : <span className="text-[11px] bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 rounded-full px-2 py-0.5 whitespace-nowrap">❌ Hal bo'lmadi</span>;
}

function Transcript({ turns }) {
  return (
    <div className="space-y-2">
      {(turns || []).map((t, i) => t.role === 'tool' ? (
        <div key={i} className="text-[11px] text-gray-400 pl-1">⚙ {t.text}</div>
      ) : t.role === 'operator' ? (
        <div key={i} className="flex">
          <div className="max-w-[85%] rounded-2xl rounded-bl-sm px-3 py-2 text-sm bg-amber-50 dark:bg-amber-900/30 text-gray-900 dark:text-gray-100 border border-amber-200 dark:border-amber-800">
            <div className="text-[10px] mb-0.5 text-amber-600 dark:text-amber-400">👨‍💼 Mutaxassis (odam) · {fmtAt(t.at)}</div>
            <span translate="no" className="whitespace-pre-wrap">{t.text}</span>
          </div>
        </div>
      ) : (
        <div key={i} className={`flex ${t.role === 'agent' ? '' : 'justify-end'}`}>
          <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${t.role === 'agent' ? 'rounded-bl-sm bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' : 'rounded-br-sm bg-primary-600 text-white'}`}>
            <div className={`text-[10px] mb-0.5 ${t.role === 'agent' ? 'text-gray-400' : 'text-primary-100'}`}>{t.role === 'agent' ? '🤖 Agent' : '👤 Mijoz'} · {fmtAt(t.at)}</div>
            <span translate="no">{t.text}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CallCenterPanel({ authFetch, apiUrl, token, projects = [], initialProjectId = null }) {
  const afRef = useRef(authFetch); afRef.current = authFetch;
  const [pid, setPid] = useState(() => {
    const ids = (projects || []).map(p => String(p.id));
    return initialProjectId && ids.includes(String(initialProjectId)) ? String(initialProjectId) : (ids[0] || '');
  });
  const [view, setView] = useState('dash');
  const [ov, setOv] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [openS, setOpenS] = useState(null);
  const [agentData, setAgentData] = useState(null);   // {agent, voices, models}
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [bal, setBal] = useState(null);           // 💰 Call Center kaliti balansi (faqat egasi/admin)
  const [orKey, setOrKey] = useState('');
  const [keySaving, setKeySaving] = useState(false);
  const audioRef = useRef(null);
  const api = (path, opts) => afRef.current(`${apiUrl}${path}`, opts);
  const post = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });

  useEffect(() => { if (!pid && projects && projects.length) setPid(String(projects[0].id)); }, [projects, pid]);

  const loadBal = async () => { if (!pid) return; try { const r = await api(`/callcenter/${pid}/balance`); if (r.ok) setBal(await r.json()); } catch (e) { } };
  const loadOv = async () => { if (!pid) return; try { const r = await api(`/callcenter/${pid}/overview`); if (r.ok) setOv(await r.json()); } catch (e) { } };
  const loadSessions = async () => { if (!pid) return; try { const r = await api(`/callcenter/${pid}/sessions?limit=50`); if (r.ok) setSessions(await r.json()); } catch (e) { } };
  const loadAgent = async () => {
    if (!pid) return;
    try { const r = await api(`/callcenter/${pid}/agent`); if (r.ok) { const d = await r.json(); setAgentData(d); setForm({ ...d.agent }); } } catch (e) { }
  };
  const openSession = async (id) => {
    setOpenId(id); setOpenS(null);
    try { const r = await api(`/callcenter/sessions/${id}`); if (r.ok) setOpenS(await r.json()); } catch (e) { }
  };
  useEffect(() => {
    setOv(null); setSessions(null); setOpenId(null); setOpenS(null); setAgentData(null); setForm(null); setBal(null); setOrKey('');
    loadOv(); loadSessions(); loadAgent(); loadBal();
    // eslint-disable-next-line
  }, [pid]);

  const agent = agentData && agentData.agent;
  // ⏳ Kalit muddati (OpenRouter /key expires_at): «faqat o'z kaliti» loyihada muddat tugasa AI to'xtaydi.
  const expDays = bal && bal.expiresAt ? Math.ceil((new Date(bal.expiresAt).getTime() - Date.now()) / 86400000) : null;
  const balBad = !!(bal && (bal.error || (bal.left != null && bal.left < 1) || (expDays != null && expDays <= 3)));

  // 🔑 Loyihaning o'z OpenRouter kaliti: '' — olib tashlash (platforma kalitiga qaytadi). Server kalitni tekshiradi, qaytarmaydi.
  const saveKeyRequired = async (on) => {
    if (on && !(agent && agent.own_key) && !window.confirm('Loyiha kaliti hali qo\'yilmagan — yoqsangiz, kalit qo\'yilguncha Call Center AI ishlamaydi. Davom etilsinmi?')) return;
    setKeySaving(true);
    try {
      const r = await api(`/callcenter/${pid}/agent`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ or_key_required: on }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setAgentData(a => ({ ...a, agent: d.agent })); setForm(f => ({ ...f, or_key_required: d.agent.or_key_required }));
      toast.success(on ? 'Endi faqat loyiha kaliti ishlatiladi' : 'Kalit bo\'lmasa platforma kaliti ishlatiladi'); loadBal();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setKeySaving(false); }
  };
  const saveKey = async (value) => {
    if (value === '' && !window.confirm('Loyiha kaliti olib tashlansinmi? Call Center platforma kalitiga qaytadi.')) return;
    setKeySaving(true);
    try {
      const r = await api(`/callcenter/${pid}/agent`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ or_key: value }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi', { duration: 8000 }); return; }
      setAgentData(a => ({ ...a, agent: d.agent })); setForm(f => ({ ...f, own_key: d.agent.own_key })); setOrKey('');
      toast.success(value ? 'Kalit saqlandi — Call Center endi shu hisobdan ishlaydi' : 'Platforma kalitiga qaytildi'); loadBal();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setKeySaving(false); }
  };

  const saveAgent = async () => {
    setSaving(true);
    try {
      const r = await api(`/callcenter/${pid}/agent`, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: form.name, enabled: !!Number(form.enabled), greeting: form.greeting, instructions: form.instructions, knowledge: form.knowledge, voice: form.voice || null, llm_model: form.llm_model || null }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setAgentData(a => ({ ...a, agent: d.agent })); setForm({ ...d.agent }); toast.success('Agent saqlandi'); loadOv();
    } catch (e) { toast.error('Saqlanmadi'); } finally { setSaving(false); }
  };
  const listen = async () => {
    setPreview({ busy: true });
    try {
      const r = await post(`/callcenter/${pid}/preview`, { text: form.greeting, voice: form.voice || null });
      const d = await r.json();
      if (!r.ok) { setPreview({ error: d.error || 'Xato' }); return; }
      try { if (audioRef.current) audioRef.current.pause(); } catch (e) { }
      const a = new Audio(`data:audio/mpeg;base64,${d.audio}`); audioRef.current = a; a.play().catch(() => { });
      setPreview({ seconds: d.seconds, cost: d.cost, voice: d.voice });
    } catch (e) { setPreview({ error: 'Ulanib bo\'lmadi' }); }
  };
  useEffect(() => () => { try { if (audioRef.current) audioRef.current.pause(); } catch (e) { } }, []);

  const makeTask = async (s) => {
    if (!window.confirm('Shu suhbat bo\'yicha Khorezmai vazifasi yaratilsinmi? (agent hal qila olmagan ehtiyojni ilovada hal qilish)')) return;
    try {
      const r = await post(`/callcenter/sessions/${s.id}/task`, {});
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Yaratilmadi'); return; }
      toast.success(`Vazifa #${d.taskId} yaratildi`);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); }
  };

  if (!projects || !projects.length) return <div className="p-8 text-center text-gray-500">Loyiha yo'q.</div>;

  const TABS = [['dash', '📊 Umumiy'], ['conv', '💬 Suhbatlar'], ['agent', '🤖 Agent'], ['faq', '📚 Savol-javoblar'], ['tg', '✈️ Telegram'], ['test', '🧪 Sinov'], ['phone', '☎️ Telefon']];
  const maxDay = ov && ov.days.length ? Math.max(...ov.days.map(d => d.n), 1) : 1;

  return (
    <div>
      <div className="flex items-center gap-3 mb-2 flex-wrap">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl shrink-0">☎️ Call Center — AI operator</h1>
        <select className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          value={pid} onChange={(e) => setPid(e.target.value)}>
          {projects.map(p => <option key={p.id} value={String(p.id)} translate="no">{p.name}</option>)}
        </select>
        {ov && (
          <span className="inline-flex items-center gap-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-3 py-1.5 text-gray-500 dark:text-gray-400">
            <span className={`w-2 h-2 rounded-full inline-block ${ov.agent.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
            Agent: <b className="text-gray-700 dark:text-gray-300" translate="no">{ov.agent.name}</b> · Ovoz: <b className="text-gray-700 dark:text-gray-300">{ov.agent.voice || 'standart'}</b>
            {!ov.agent.saved && <span className="text-amber-600">· hali sozlanmagan</span>}
          </span>
        )}
        {bal && !bal.hidden && (
          <span title={bal.own ? 'Loyihaning o\'z OpenRouter kaliti' : 'Platforma OpenRouter kaliti'}
            className={`inline-flex items-center gap-1 text-xs border rounded-full px-3 py-1.5 ${balBad ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'}`}>
            💰 {bal.error ? 'kalit ishlamayapti' : bal.left != null ? <b>{fmtUsd(bal.left)}</b> : '—'} · {bal.own ? '🔑 loyiha kaliti' : 'platforma kaliti'}{agent && Number(agent.or_key_required) ? ' 🔒' : ''}{expDays != null && expDays <= 7 ? ` · ⏳ ${Math.max(0, expDays)} kun` : ''}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Mijozlaringizga telefon va Telegram bot orqali javob beradigan AI operator: savol-javoblar bazasi va bilimlardan javob beradi, bilmaganini
        to'qimaydi — mutaxassisga yuboradi (Telegram'da javobingiz mijozga ketadi va bazaga qo'shiladi). Har suhbat yoziladi va AI tahlil qiladi.
      </p>

      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4 overflow-x-auto">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => { setView(k); if (k === 'conv') loadSessions(); if (k === 'dash') loadOv(); }}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition whitespace-nowrap ${view === k ? 'border-primary-700 text-primary-800 dark:text-primary-300' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>{l}</button>
        ))}
      </div>

      {view === 'dash' && (
        !ov ? <div className="text-sm text-gray-400">Yuklanmoqda…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {[['Suhbatlar (30 kun)', ov.total30], ['Hal bo\'ldi', ov.successRate == null ? '—' : `${ov.successRate}%`], ['O\'rtacha davomiylik', fmtDur(ov.avgDuration)],
                ['Qayta qo\'ng\'iroq so\'ralgan', ov.callbacks30], ['AI sarfi (30 kun)', fmtUsd(ov.cost30)]].map(([l, v]) => (
                <div key={l} className={card}><div className="text-xs text-gray-500 dark:text-gray-400">{l}</div><div className="text-2xl font-semibold text-gray-900 dark:text-white mt-1">{v}</div></div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`${card} md:col-span-2`}>
                <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Kunlik suhbatlar (14 kun)</p>
                {ov.days.length === 0 ? <div className="text-sm text-gray-400 py-8 text-center">Hali suhbat yo'q — 🧪 Sinov bo'limida qo'ng'iroq qilib ko'ring.</div> : (
                  <div className="flex items-end gap-1 h-28">
                    {ov.days.map(d => (
                      <div key={d.day} className="flex-1 flex flex-col items-center justify-end h-full" title={`${d.day}: ${d.n} ta, ${d.ok} tasi hal bo'ldi`}>
                        <div className="w-full rounded-t bg-gradient-to-t from-primary-600 to-primary-300" style={{ height: `${Math.max(4, d.n * 100 / maxDay)}%` }} />
                        <div className="text-[9px] text-gray-400 mt-0.5">{d.day.slice(8, 10)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={card}>
                <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Mavzular</p>
                {ov.topics.length === 0 ? <div className="text-sm text-gray-400">—</div> : ov.topics.map(t => (
                  <div key={t.topic} className="flex items-center justify-between text-sm py-1"><span className="text-gray-700 dark:text-gray-300 truncate">{t.topic}</span><span className="text-gray-400 ml-2">{t.n}</span></div>
                ))}
              </div>
            </div>
            <div className={card}>
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Oxirgi suhbatlar</p>
              {ov.recent.length === 0 ? <div className="text-sm text-gray-400">—</div> : ov.recent.map(s => (
                <button key={s.id} onClick={() => { setView('conv'); loadSessions(); openSession(s.id); }}
                  className="w-full flex items-center gap-3 text-left text-sm py-1.5 border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <span className="text-gray-400 w-24 shrink-0">{fmtTime(s.startedAt)}</span>
                  <span className="flex-1 truncate text-gray-700 dark:text-gray-300">{s.topic || (s.status === 'active' ? 'davom etmoqda…' : '—')}</span>
                  {okChip(s.success)}
                </button>
              ))}
            </div>
          </div>
        )
      )}

      {view === 'conv' && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className={`${card} lg:col-span-2 max-h-[70vh] overflow-y-auto`}>
            {!sessions ? <div className="text-sm text-gray-400">Yuklanmoqda…</div> : sessions.length === 0 ? <div className="text-sm text-gray-400">Hali suhbat yo'q.</div> : sessions.map(s => (
              <button key={s.id} onClick={() => openSession(s.id)}
                className={`w-full text-left rounded-lg px-3 py-2 mb-1 ${openId === s.id ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'}`}>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{fmtTime(s.startedAt)}</span><span>·</span><span translate="no" className="truncate">{s.channel === 'test' ? `🧪 sinov (${s.mode === 'text' ? 'matn' : 'ovoz'})` : s.channel === 'telegram' ? `✈️ ${s.caller || ''}` : `☎️ ${s.caller || ''}`}</span>
                  <span>·</span><span>{fmtDur(s.duration)}</span>{s.callback && <span title={s.channel === 'telegram' ? 'Savol mutaxassisga yuborilgan' : "Qayta qo'ng'iroq so'ralgan"}>📲</span>}
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-sm text-gray-800 dark:text-gray-200 truncate">{s.topic || (s.status === 'active' ? 'davom etmoqda…' : '—')}</span>
                  {okChip(s.success)}
                </div>
              </button>
            ))}
          </div>
          <div className={`${card} lg:col-span-3`}>
            {!openId ? <div className="text-sm text-gray-400">Chapdan suhbatni tanlang.</div> : !openS ? <div className="text-sm text-gray-400">Yuklanmoqda…</div> : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 dark:text-white">Suhbat #{openS.id}</span>
                  {okChip(openS.success)}
                  <span className="text-xs text-gray-400">{fmtTime(openS.startedAt)} · {fmtDur(openS.duration)} · {fmtUsd(openS.cost)}</span>
                </div>
                {openS.summary && <div className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3"><b>🧠 Xulosa:</b> {openS.summary}</div>}
                {Object.keys(openS.collected || {}).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(openS.collected).map(([k, v]) => <span key={k} className="text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 rounded-full px-2.5 py-1" translate="no">{FIELD_LABEL[k] || k}: {v}</span>)}
                  </div>
                )}
                {openS.callback && <div className="text-xs text-amber-700 dark:text-amber-400">{openS.channel === 'telegram' ? '📲 Savol mutaxassisga yuborilgan (📚 Savol-javoblar → ❓ Javobsiz savollar).' : '📲 Mijoz mutaxassis qayta qo\'ng\'iroq qilishini kutmoqda.'}</div>}
                <Transcript turns={openS.turns} />
                {openS.success === false && (
                  <button onClick={() => makeTask(openS)} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">🔧 Shundan vazifa yaratish</button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {view === 'agent' && (
        !form ? <div className="text-sm text-gray-400">Yuklanmoqda…</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`${card} lg:col-span-2 space-y-3`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block md:col-span-2"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Agent nomi</span>
                  <input className={inp} value={form.name || ''} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} /></label>
                <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={!!Number(form.enabled)} onChange={(e) => setForm(f => ({ ...f, enabled: e.target.checked ? 1 : 0 }))} /> Yoqilgan</label>
              </div>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Salomlashish (birinchi gap)</span>
                <input className={inp} value={form.greeting || ''} onChange={(e) => setForm(f => ({ ...f, greeting: e.target.value }))} /></label>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">Ko'rsatma — kim, qanday gaplashadi</span>
                <textarea rows={5} className={inp} value={form.instructions || ''} onChange={(e) => setForm(f => ({ ...f, instructions: e.target.value }))} /></label>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">📚 Bilimlar — agent faqat shunga tayanadi</span>
                <textarea rows={10} className={`${inp} font-mono text-xs`} value={form.knowledge || ''} onChange={(e) => setForm(f => ({ ...f, knowledge: e.target.value }))}
                  placeholder={"Ish vaqti: 9:00–21:00, har kuni.\nYetkazish: Toshkent ichida 40–60 daqiqa, 15 000 so'm.\nTo'lovni qaytarish: bekor qilingan buyurtma pulini 3 ish kunida qaytaramiz.\n…"} />
                <span className="text-[11px] text-gray-400">{(form.knowledge || '').length} / 30 000 belgi · bu yerda va 📚 Savol-javoblarda yo'q narsani agent aytmaydi — mutaxassisga yo'naltiradi</span></label>
              <button onClick={saveAgent} disabled={saving} className="px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium disabled:opacity-50">{saving ? '⏳' : 'Saqlash'}</button>
              <span className="text-[11px] text-gray-400 ml-2">Saqlash — faqat loyiha egasi.</span>
            </div>
            <div className={`${card} space-y-3`}>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">🔊 Ovoz</span>
                <select className={inp} value={form.voice || ''} onChange={(e) => setForm(f => ({ ...f, voice: e.target.value || null }))}>
                  <option value="">standart (Boshqaruvchi ovozi)</option>
                  {(agentData.voices || []).map(v => <option key={v.id} value={v.id}>{v.id} — {v.style}</option>)}
                </select></label>
              <div>
                <button onClick={listen} disabled={preview && preview.busy} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50">{preview && preview.busy ? '⏳' : '▶ Salomni eshitish'}</button>
                <div className={`text-[11px] mt-1 ${preview && preview.error ? 'text-red-500' : 'text-gray-400'}`}>
                  {preview && preview.error ? preview.error : preview && preview.seconds ? `${preview.voice}: ${preview.seconds} s · ~${fmtUsd(preview.cost)}` : '~$0.001'}
                </div>
              </div>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">🧠 Miya (model)</span>
                <select className={inp} value={form.llm_model || ''} onChange={(e) => setForm(f => ({ ...f, llm_model: e.target.value || null }))}>
                  <option value="">standart (telefon — flash-lite, Telegram — flash)</option>
                  {(agentData.models || []).map(m => <option key={m} value={m}>{m.replace('google/', '')}</option>)}
                </select>
                <span className="text-[11px] text-gray-400">flash-lite: javob ~1 s, arzon. flash: aqlliroq, mijoz tilini (ruscha, kirill) yaxshi ushlaydi, 2–6 s.</span></label>
              <div className="border-t border-gray-100 dark:border-gray-700 pt-3 space-y-2">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">🔑 OpenRouter kaliti (AI hisobi)</span>
                <div className="text-xs text-gray-600 dark:text-gray-300">
                  {agent && agent.own_key
                    ? <>✅ Loyihaning o'z kaliti <code translate="no">{agent.own_key}</code> — javob, ovoz, tahlil shu hisobdan; platforma kunlik limitiga sanalmaydi.</>
                    : Number(agent && agent.or_key_required) ? '⛔ Kalit qo\'yilmagan — bu loyiha faqat o\'z kaliti bilan ishlaydi, AI hozir to\'xtagan.'
                    : 'Platforma kaliti ishlatilmoqda. Mijozning o\'z kalitini qo\'ysangiz — sarf uning hisobidan.'}
                </div>
                <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                  <input type="checkbox" className="mt-0.5" checked={!!Number(agent && agent.or_key_required)} disabled={keySaving} onChange={(e) => saveKeyRequired(e.target.checked)} />
                  <span>🔒 Faqat loyiha kaliti — platforma kalitiga hech qachon o'tmasin (kalit ishlamasa AI to'xtaydi, savollar adminga o'tadi, sizga xabar keladi)</span>
                </label>
                {bal && !bal.hidden && (
                  <div className={`text-xs rounded-lg px-3 py-2 ${balBad ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-gray-50 dark:bg-gray-700/50 text-gray-700 dark:text-gray-300'}`}>
                    {bal.error ? <>⚠️ {bal.error}</> : (
                      <>💰 Qoldiq: <b>{bal.left != null ? fmtUsd(bal.left) : '—'}</b>{bal.total != null ? ` (jami $${Number(bal.total).toFixed(2)})` : ''}
                        {bal.usageMonth != null ? ` · bu oy ${fmtUsd(bal.usageMonth)}` : ''}{bal.usageDay != null ? ` · bugun ${fmtUsd(bal.usageDay)}` : ''}
                        {bal.limit != null ? ` · kalit limiti: ${fmtUsd(bal.limitLeft)} / $${bal.limit}` : ''}
                        {bal.expiresAt ? ` · ⏳ muddati ${fmtTime(bal.expiresAt).slice(0, 5)} (${expDays > 0 ? `${expDays} kun qoldi` : 'TUGAGAN'})` : ' · muddatsiz'}
                        {expDays != null && expDays <= 3 && <div className="mt-1">Kalit muddati tugayapti — mijozdan yangi (muddatsiz, oylik limitli) kalit oling</div>}
                        {bal.left != null && bal.left < 1 && <div className="mt-1">Kredit tugayapti — openrouter.ai/settings/credits</div>}</>
                    )}
                    <button onClick={loadBal} className="ml-2 text-[11px] underline opacity-70">yangilash</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <input type="password" autoComplete="off" className={inp} placeholder="sk-or-v1-…" value={orKey} onChange={(e) => setOrKey(e.target.value)} />
                  <button onClick={() => saveKey(orKey.trim())} disabled={!orKey.trim() || keySaving} className="px-3 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm whitespace-nowrap disabled:opacity-50">{keySaving ? '⏳' : 'Kalitni saqlash'}</button>
                </div>
                {agent && agent.own_key && <button onClick={() => saveKey('')} disabled={keySaving} className="text-[11px] text-red-600 hover:underline">Loyiha kalitini olib tashlash</button>}
                <span className="block text-[11px] text-gray-400">Saqlashdan oldin OpenRouter'da tekshiriladi; shifrlangan saqlanadi va qayta ko'rsatilmaydi. Faqat loyiha egasi.</span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-3">
                <b>Agent o'zi qiladi:</b> ism, telefon, buyurtma raqami, manzilni yozib oladi; javob bilimlarda bo'lmasa — mutaxassis qayta qo'ng'iroq qilishini aytadi
                (haqiqiy qo'ng'iroqda loyiha egasiga Telegram/push boradi); xayrlashsa suhbatni tugatadi. Parol, karta raqami, SMS kodini hech qachon so'ramaydi.
              </div>
            </div>
          </div>
        )
      )}

      {view === 'faq' && <FaqPanel key={pid} pid={pid} api={api} post={post} />}

      {view === 'tg' && <TelegramPanel key={pid} pid={pid} api={api} post={post} />}

      {view === 'test' && <CallTest key={pid} pid={pid} apiUrl={apiUrl} token={token} api={api} post={post} agent={agent} onDone={() => { loadOv(); loadSessions(); }} />}

      {view === 'phone' && (
        <div className={`${card} max-w-2xl space-y-2 text-sm text-gray-700 dark:text-gray-300`}>
          <p className="font-semibold">☎️ Telefon raqami — hali ulanmagan</p>
          <p>Agent hozir brauzer orqali ishlaydi (🧪 Sinov). Haqiqiy raqamga qo'ng'iroq qilinganda javob berishi uchun <b>SIP provayder</b> kerak:
            raqam + ovoz oqimini serverga uzatish (masalan, mahalliy operatorning SIP-trunki yoki ovoz oqimi API'si bor xalqaro provayder).</p>
          <p>Ulanganda qo'ng'iroq aynan shu agentga tushadi: suhbat, xulosa, statistika shu yerda chiqadi, «qayta qo'ng'iroq» so'rovlari sizga Telegram'ga keladi.</p>
          <p className="text-xs text-gray-400">Provayder tanlash va shartnoma — loyiha egasi qarori (Boshqaruvchi shu ishni rejaga karta qilib qo'yishi mumkin).</p>
        </div>
      )}
    </div>
  );
}

/** 🧪 Sinov: 📞 jonli qo'ng'iroq (brauzer mikrofoni) yoki 💬 matnli suhbat. */
function CallTest({ pid, apiUrl, token, api, post, agent, onDone }) {
  const [sess, setSess] = useState(null);          // {id, mode}
  const [turns, setTurns] = useState([]);          // [{role, text}]
  const [call, setCall] = useState(null);          // {state, secs, muted}
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);          // {ms}
  const engineRef = useRef(null);
  const sidRef = useRef(null);
  const busyRef = useRef(false);
  const chainRef = useRef(Promise.resolve());
  const timerRef = useRef(null);
  const endRef = useRef(null);
  const t0Ref = useRef(0);

  useEffect(() => { if (endRef.current) endRef.current.scrollIntoView({ block: 'nearest' }); }, [turns]);

  // Jonli voqealar (faqat sinov davomida).
  useEffect(() => {
    if (!token || !sess) return undefined;
    const s = io(apiUrl || '', { auth: { token } });
    const mine = (d) => d && Number(d.sessionId) === Number(sidRef.current);
    s.on('cc:audio', (d) => { if (mine(d) && engineRef.current && d.audio) engineRef.current.playMp3(d.audio); });
    s.on('cc:transcript', (d) => { if (mine(d) && d.text) setTurns(t => t.map(x => (x.pending ? { ...x, text: d.text, pending: false } : x))); });
    s.on('cc:tool', (d) => { if (mine(d)) setTurns(t => [...t, { role: 'tool', text: `${d.name} ${JSON.stringify(d.args || {})}` }]); });
    return () => { s.disconnect(); };
    // eslint-disable-next-line
  }, [token, apiUrl, sess && sess.id]);

  const finish = async (id) => {
    if (!id) return;
    try {
      const r = await post(`/callcenter/sessions/${id}/end`, {});
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.session) toast.success(d.session.summary ? `Suhbat yozildi: ${d.session.topic || ''}` : 'Suhbat yozildi');
    } catch (e) { }
    onDone && onDone();
  };

  const turn = async ({ audioBlob = null, textIn = null }) => {
    const id = sidRef.current; if (!id) return;
    busyRef.current = true; setBusy(true);
    if (engineRef.current) engineRef.current.setBusy(true);
    if (audioBlob) setTurns(t => [...t, { role: 'caller', text: '🎤 …', pending: true }]);
    else setTurns(t => [...t, { role: 'caller', text: textIn }]);
    const started = Date.now();
    try {
      const body = audioBlob ? { audio: await blobToBase64(audioBlob) } : { text: textIn };
      const r = await post(`/callcenter/sessions/${id}/turn`, body);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setTurns(t => t.filter(x => !x.pending));
        toast.error(d.error || 'Xatolik');
        if (r.status === 429) hangup();
        return;
      }
      setTurns(t => [...t.map(x => (x.pending ? { ...x, text: d.transcript || x.text, pending: false } : x)), { role: 'agent', text: d.reply || '…' }]);
      setLast({ ms: Date.now() - started });
      if (d.ended) setTimeout(() => hangup(), audioBlob ? 3500 : 300);   // xayrlashuv o'ynab bo'lsin
    } catch (e) { toast.error('Ulanib bo\'lmadi'); }
    finally {
      busyRef.current = false; setBusy(false);
      if (engineRef.current) engineRef.current.setBusy(false);
    }
  };

  const startVoice = async () => {
    // AudioContext + mikrofon — shu bosishning o'zida (iOS talabi), keyin serverdan salom.
    const eng = new LiveCallEngine({
      onState: (st) => setCall(c => (c ? { ...c, state: st } : c)),
      onBargeIn: () => { if (sidRef.current) post(`/callcenter/sessions/${sidRef.current}/cancel`, {}).catch(() => { }); },
      onUtterance: (blob) => {
        if (busyRef.current && sidRef.current) { post(`/callcenter/sessions/${sidRef.current}/cancel`, {}).catch(() => { }); if (engineRef.current) engineRef.current.stopPlayback(); }
        chainRef.current = chainRef.current.then(() => turn({ audioBlob: blob })).catch(() => { });
      },
    });
    try { await eng.start(); } catch (e) {
      toast.error(micErrorText(e), { duration: 8000 });
      return;
    }
    engineRef.current = eng;
    eng.setBusy(true);
    setCall({ state: 'thinking', secs: 0, muted: false });
    try {
      const r = await post(`/callcenter/${pid}/sessions`, { mode: 'voice' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Boshlanmadi'); eng.stop(); engineRef.current = null; setCall(null); return; }
      sidRef.current = d.sessionId; setSess({ id: d.sessionId, mode: 'voice' });
      setTurns([{ role: 'agent', text: d.greeting }]);
      if (d.audio) eng.playMp3(d.audio);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); eng.stop(); engineRef.current = null; setCall(null); return; }
    finally { eng.setBusy(false); }
    t0Ref.current = Date.now();
    timerRef.current = setInterval(() => setCall(c => (c ? { ...c, secs: Math.floor((Date.now() - t0Ref.current) / 1000) } : c)), 1000);
  };

  const startText = async () => {
    try {
      const r = await post(`/callcenter/${pid}/sessions`, { mode: 'text' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Boshlanmadi'); return; }
      sidRef.current = d.sessionId; setSess({ id: d.sessionId, mode: 'text' });
      setTurns([{ role: 'agent', text: d.greeting }]);
    } catch (e) { toast.error('Ulanib bo\'lmadi'); }
  };

  const hangup = () => {
    const eng = engineRef.current; engineRef.current = null;
    if (eng) eng.stop();
    clearInterval(timerRef.current);
    const id = sidRef.current; sidRef.current = null;
    setCall(null); setSess(null);
    finish(id);
  };
  useEffect(() => () => { const eng = engineRef.current; if (eng) eng.stop(); clearInterval(timerRef.current); if (sidRef.current) post(`/callcenter/sessions/${sidRef.current}/end`, {}).catch(() => { }); }, []);   // eslint-disable-line

  const sendText = () => { const v = text.trim(); if (!v || busy) return; setText(''); chainRef.current = chainRef.current.then(() => turn({ textIn: v })); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className={`${card} lg:col-span-2`}>
        {!sess ? (
          <div className="py-8 text-center space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">Mijoz sifatida qo'ng'iroq qilib ko'ring — agent <b translate="no">{agent ? agent.name : ''}</b> javob beradi.</p>
            <div className="flex justify-center gap-3 flex-wrap">
              <button onClick={startVoice} className="px-5 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow">📞 Jonli qo'ng'iroq</button>
              <button onClick={startText} className="px-5 py-3 rounded-full border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold">💬 Matnli sinov</button>
            </div>
            <p className="text-[11px] text-gray-400">Gapiring — to'xtaganingizni o'zi sezadi. Agent gapirayotganda gapirsangiz to'xtaydi. Quloqchin bilan eng yaxshi.</p>
          </div>
        ) : (
          <div className="flex flex-col h-[60vh]">
            <div className="flex-1 overflow-y-auto pr-1"><Transcript turns={turns.map(t => ({ ...t, at: undefined }))} /><div ref={endRef} /></div>
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-2">
              {sess.mode === 'voice' && call ? (
                <CallBar state={call.state} engine={engineRef.current} secs={call.secs} muted={call.muted}
                  note={last ? `oxirgi javob ${(last.ms / 1000).toFixed(1)} s` : null}
                  onMute={() => { const m = !call.muted; if (engineRef.current) engineRef.current.setMuted(m); setCall(c => ({ ...c, muted: m })); }}
                  onInterrupt={() => { if (engineRef.current) engineRef.current.stopPlayback(); if (sidRef.current) post(`/callcenter/sessions/${sidRef.current}/cancel`, {}).catch(() => { }); }}
                  onHangup={hangup} />
              ) : (
                <div className="flex gap-2">
                  <input className={inp} value={text} onChange={(e) => setText(e.target.value)} placeholder="Mijoz sifatida yozing…"
                    onKeyDown={(e) => { if (e.key === 'Enter') sendText(); }} />
                  <button onClick={sendText} disabled={busy} className="px-4 rounded-lg bg-primary-700 text-white text-sm disabled:opacity-50">{busy ? '⏳' : 'Yuborish'}</button>
                  <button onClick={hangup} className="px-3 rounded-lg bg-red-600 text-white text-sm">Tugatish</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className={`${card} text-xs text-gray-500 dark:text-gray-400 space-y-2`}>
        <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Sinov haqida</p>
        <p>Sinov suhbati ham yoziladi va tahlil qilinadi (💬 Suhbatlar). «Qayta qo'ng'iroq» so'rovi sinovda egaga yuborilmaydi.</p>
        <p>Narx: bitta javob ~$0.001–0.003 (tinglash + javob + ovoz). Loyiha kunlik chegarasi amal qiladi.</p>
        <p>Agent bilmagan savolni bering — to'qimasligini, ism/raqamni so'rab qayta qo'ng'iroqqa yozishini tekshiring.</p>
      </div>
    </div>
  );
}

const SRC_LABEL = { import: '📥 import', ui: '✍️ qo\'lda', telegram: '✈️ botdan', learned: '🎓 javobdan o\'rgandi' };
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
  fr.onerror = () => reject(fr.error);
  fr.readAsDataURL(file);
});

/** 📚 Savol-javoblar: baza (qo'shish/tahrir/o'chirish), import (.docx/matn → oldindan ko'rish → ✅), ❓ javobsiz savollar. */
function FaqPanel({ pid, api, post }) {
  const [rows, setRows] = useState(null);
  const [qs, setQs] = useState(null);
  const [nq, setNq] = useState({ question: '', answer: '' });
  const [edit, setEdit] = useState(null);       // {id, question, answer}
  const [filter, setFilter] = useState('');
  const [imp, setImp] = useState(null);         // {text, items, picked, via, busy, name}
  const [ans, setAns] = useState({});           // qid -> {text, learn}
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);
  const jreq = (path, method, body) => api(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const load = async () => { try { const r = await api(`/callcenter/${pid}/faq`); if (r.ok) setRows(await r.json()); } catch (e) { } };
  const loadQ = async () => { try { const r = await api(`/callcenter/${pid}/questions?status=open`); if (r.ok) setQs(await r.json()); } catch (e) { } };
  useEffect(() => { load(); loadQ(); const t = setInterval(loadQ, 30000); return () => clearInterval(t); }, [pid]);   // eslint-disable-line

  const add = async () => {
    if (!nq.question.trim() || !nq.answer.trim()) { toast.error('Savol ham, javob ham kerak'); return; }
    setBusy('add');
    try {
      const r = await post(`/callcenter/${pid}/faq`, nq);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Qo\'shilmadi'); return; }
      setNq({ question: '', answer: '' }); toast.success(`Qo'shildi (№${d.id})`); load();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setBusy(''); }
  };
  const saveEdit = async () => {
    setBusy('edit');
    try {
      const r = await jreq(`/callcenter/faq/${edit.id}`, 'PUT', { question: edit.question, answer: edit.answer });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi'); return; }
      setEdit(null); toast.success('Saqlandi'); load();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setBusy(''); }
  };
  const toggle = async (f) => { try { const r = await jreq(`/callcenter/faq/${f.id}`, 'PUT', { enabled: !f.enabled }); if (r.ok) load(); } catch (e) { } };
  const del = async (f) => {
    if (!window.confirm(`№${f.id} «${f.question}» o'chirilsinmi?`)) return;
    try { const r = await api(`/callcenter/faq/${f.id}`, { method: 'DELETE' }); if (r.ok) { toast.success('O\'chirildi'); load(); } else toast.error('O\'chirilmadi'); } catch (e) { }
  };
  const parse = async (body, name) => {
    setImp(i => ({ ...(i || {}), busy: true, name }));
    try {
      const r = await post(`/callcenter/${pid}/faq/parse`, body);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Ajratib bo\'lmadi'); setImp(i => ({ ...(i || {}), busy: false })); return; }
      if (!d.items.length) toast.error('Savol-javob topilmadi — «Savol: …» keyingi qatorda «Javob: …» ko\'rinishida yozing');
      setImp(i => ({ ...(i || {}), busy: false, items: d.items, via: d.via, picked: d.items.map(() => true) }));
    } catch (e) { toast.error('Ulanib bo\'lmadi'); setImp(i => ({ ...(i || {}), busy: false })); }
  };
  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error('Fayl juda katta (≤10 MB)'); return; }
    parse({ fileName: f.name, data: await fileToBase64(f) }, f.name);
  };
  const doImport = async () => {
    const items = imp.items.filter((_, i) => imp.picked[i]);
    if (!items.length) return;
    setBusy('imp');
    try {
      const r = await post(`/callcenter/${pid}/faq/import`, { items });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Qo\'shilmadi'); return; }
      toast.success(`${d.added} ta qo'shildi${d.updated ? `, ${d.updated} ta yangilandi` : ''}`); setImp(null); load();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setBusy(''); }
  };
  const answer = async (q) => {
    const a = ans[q.id] || {};
    if (!(a.text || '').trim()) { toast.error('Javob yozing'); return; }
    setBusy(`q${q.id}`);
    try {
      const r = await post(`/callcenter/questions/${q.id}/answer`, { answer: a.text, addToFaq: a.learn !== false });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Yuborilmadi'); return; }
      toast.success(d.learned && d.learned.ok ? 'Mijozga yuborildi va bazaga qo\'shildi' : 'Mijozga yuborildi');
      setAns(x => { const y = { ...x }; delete y[q.id]; return y; }); loadQ(); load();
    } catch (e) { toast.error('Ulanib bo\'lmadi'); } finally { setBusy(''); }
  };
  const dismiss = async (q) => { try { const r = await post(`/callcenter/questions/${q.id}/dismiss`, {}); if (r.ok) loadQ(); } catch (e) { } };

  const f = filter.trim().toLowerCase();
  const shown = (rows || []).filter(x => !f || `${x.question} ${x.answer}`.toLowerCase().includes(f));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        {qs && qs.length > 0 && (
          <div className={`${card} border-amber-300 dark:border-amber-700`}>
            <p className="font-semibold text-gray-800 dark:text-gray-200 mb-1">❓ Javobsiz savollar ({qs.length})</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Bot bilmagan savollar. Javobingiz Telegram orqali mijozga ketadi; belgilansa bazaga ham qo'shiladi — keyingi safar bot o'zi javob beradi. Bot adminlari xuddi shuni bot ichida (reply) qiladi.</p>
            <div className="space-y-3">
              {qs.map(q => {
                const a = ans[q.id] || {};
                return (
                  <div key={q.id} className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 space-y-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400"><span translate="no">👤 {q.customer || 'mijoz'}</span> · {fmtTime(q.created_at)} · #{q.id}</div>
                    <div className="text-sm text-gray-900 dark:text-white" translate="no">«{q.question}»</div>
                    {q.reason && <div className="text-[11px] text-gray-500 dark:text-gray-400">🤖 {q.reason}</div>}
                    <textarea rows={2} className={inp} placeholder="Mijozga javob…" value={a.text || ''} onChange={(e) => setAns(x => ({ ...x, [q.id]: { ...a, text: e.target.value } }))} />
                    <div className="flex items-center gap-3 flex-wrap">
                      <button onClick={() => answer(q)} disabled={busy === `q${q.id}`} className="px-3 py-1.5 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm disabled:opacity-50">{busy === `q${q.id}` ? '⏳' : 'Javob yuborish'}</button>
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300"><input type="checkbox" checked={a.learn !== false} onChange={(e) => setAns(x => ({ ...x, [q.id]: { ...a, learn: e.target.checked } }))} /> 📚 bazaga ham qo'shish</label>
                      <button onClick={() => dismiss(q)} className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 ml-auto">🙈 Javobsiz yopish</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className={card}>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <p className="font-semibold text-gray-800 dark:text-gray-200">📚 Savol-javoblar {rows ? `(${rows.length})` : ''}</p>
            <input className={`${inp} max-w-xs`} placeholder="Qidirish…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          </div>
          {!rows ? <div className="text-sm text-gray-400">Yuklanmoqda…</div> : rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">Baza bo'sh — o'ngdan qo'shing yoki .docx import qiling.</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {shown.map(x => (edit && edit.id === x.id ? (
                <div key={x.id} className="py-3 space-y-2">
                  <input className={inp} value={edit.question} onChange={(e) => setEdit(v => ({ ...v, question: e.target.value }))} />
                  <textarea rows={4} className={inp} value={edit.answer} onChange={(e) => setEdit(v => ({ ...v, answer: e.target.value }))} />
                  <div className="flex gap-2">
                    <button onClick={saveEdit} disabled={busy === 'edit'} className="px-3 py-1.5 rounded-lg bg-primary-700 text-white text-sm disabled:opacity-50">Saqlash</button>
                    <button onClick={() => setEdit(null)} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">Bekor</button>
                  </div>
                </div>
              ) : (
                <div key={x.id} className={`py-3 ${x.enabled ? '' : 'opacity-50'}`}>
                  <div className="flex items-start gap-2">
                    <span className="text-xs text-gray-400 mt-0.5 shrink-0">№{x.id}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white" translate="no">{x.question}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap mt-1" translate="no">{x.answer}</div>
                      <div className="text-[11px] text-gray-400 mt-1">{SRC_LABEL[x.source] || x.source}{x.created_by ? ` · ${x.created_by}` : ''}{x.enabled ? '' : ' · 🚫 o\'chirilgan (bot ishlatmaydi)'}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button title="Tahrirlash" onClick={() => setEdit({ id: x.id, question: x.question, answer: x.answer })} className="px-2 py-1 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700">✏️</button>
                      <button title={x.enabled ? 'Vaqtincha o\'chirish' : 'Yoqish'} onClick={() => toggle(x)} className="px-2 py-1 text-xs rounded hover:bg-gray-100 dark:hover:bg-gray-700">{x.enabled ? '⏸' : '▶'}</button>
                      <button title="O'chirish" onClick={() => del(x)} className="px-2 py-1 text-xs rounded hover:bg-red-50 dark:hover:bg-red-900/30">🗑</button>
                    </div>
                  </div>
                </div>
              )))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-4">
        <div className={`${card} space-y-2`}>
          <p className="font-semibold text-gray-800 dark:text-gray-200">➕ Yangi savol-javob</p>
          <input className={inp} placeholder="Savol (mijoz qanday so'raydi)" value={nq.question} onChange={(e) => setNq(v => ({ ...v, question: e.target.value }))} />
          <textarea rows={4} className={inp} placeholder="Javob" value={nq.answer} onChange={(e) => setNq(v => ({ ...v, answer: e.target.value }))} />
          <button onClick={add} disabled={busy === 'add'} className="px-4 py-2 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm font-medium disabled:opacity-50">{busy === 'add' ? '⏳' : 'Qo\'shish'}</button>
        </div>
        <div className={`${card} space-y-2`}>
          <p className="font-semibold text-gray-800 dark:text-gray-200">📥 Fayldan / matndan import</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">.docx, .txt yoki .pdf — «Savol: …» va «Javob: …» ko'rinishida (mijoz bergan hujjat shunday). Boshqa ko'rinishdagi matnni AI ajratadi. Bor savolning javobi yangilanadi.</p>
          <input ref={fileRef} type="file" accept=".docx,.txt,.md,.pdf,.csv" className="hidden" onChange={onFile} />
          <div className="flex gap-2">
            <button onClick={() => fileRef.current && fileRef.current.click()} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">📄 Fayl tanlash</button>
            <button onClick={() => setImp(i => (i && i.text !== undefined ? null : { text: '' }))} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">📋 Matn qo'yish</button>
          </div>
          {imp && imp.text !== undefined && !imp.items && (
            <div className="space-y-2">
              <textarea rows={6} className={`${inp} font-mono text-xs`} placeholder={'1. Savol: Kargo puli qancha?\nJavob: Avia kargo — $9.5/kg …'} value={imp.text} onChange={(e) => setImp(i => ({ ...i, text: e.target.value }))} />
              <button onClick={() => parse({ text: imp.text }, 'matn')} disabled={imp.busy || !imp.text.trim()} className="px-3 py-1.5 rounded-lg bg-primary-700 text-white text-sm disabled:opacity-50">{imp.busy ? '⏳' : 'Ajratish'}</button>
            </div>
          )}
          {imp && imp.busy && imp.text === undefined && <div className="text-xs text-gray-400">⏳ {imp.name} o'qilmoqda…</div>}
          {imp && imp.items && imp.items.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 dark:text-gray-400">{imp.name ? <span translate="no">{imp.name}: </span> : ''}{imp.items.length} ta topildi{imp.via === 'ai' ? ' (AI ajratdi — tekshirib oling)' : ''}</div>
              <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
                {imp.items.map((x, i) => (
                  <label key={i} className="flex items-start gap-2 text-xs bg-gray-50 dark:bg-gray-700/50 rounded p-2">
                    <input type="checkbox" className="mt-0.5" checked={!!imp.picked[i]} onChange={(e) => setImp(v => ({ ...v, picked: v.picked.map((p, j) => (j === i ? e.target.checked : p)) }))} />
                    <span translate="no"><b className="text-gray-800 dark:text-gray-200">{x.question}</b><br /><span className="text-gray-500 dark:text-gray-400 whitespace-pre-wrap">{x.answer.length > 160 ? x.answer.slice(0, 160) + '…' : x.answer}</span></span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={doImport} disabled={busy === 'imp'} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50">{busy === 'imp' ? '⏳' : `✅ ${imp.picked.filter(Boolean).length} tasini qo'shish`}</button>
                <button onClick={() => setImp(null)} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300">Bekor</button>
              </div>
            </div>
          )}
        </div>
        <div className={`${card} text-xs text-gray-500 dark:text-gray-400 space-y-1`}>
          <p>Agent javobni shu bazadan oladi (narx, muddat, raqamlarni aynan), mijoz tilida (ruscha, kirill) o'girib beradi. Bazada yo'q savolni to'qimaydi — mutaxassisga yuboradi.</p>
          <p>Mijoz-kompaniya xodimi bazani Telegram botning o'zidan to'ldira oladi: ✈️ Telegram → admin havolasi.</p>
        </div>
      </div>
    </div>
  );
}

const TG_STATUS = {
  running: ['bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400', '✅ Ishlayapti'],
  starting: ['bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300', '⏳ Ulanmoqda'],
  error: ['bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400', '❌ Xato'],
  off: ['bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400', '⏸ O\'chiq'],
};

/** ✈️ Telegram kanali: bot tokeni (faqat egasi), yoqish, salom matni, mijoz xodimi uchun bir martalik admin havolasi, adminlar. */
function TelegramPanel({ pid, api, post }) {
  const [v, setV] = useState(null);
  const [token, setToken] = useState('');
  const [greeting, setGreeting] = useState('');
  const [busy, setBusy] = useState('');
  const load = async (initial = false) => {
    try {
      const r = await api(`/callcenter/${pid}/telegram`);
      if (!r.ok) return;
      const d = await r.json();
      setV(d); if (initial) setGreeting(d.greeting || '');
    } catch (e) { }
  };
  useEffect(() => { load(true); const t = setInterval(() => load(false), 15000); return () => clearInterval(t); }, [pid]);   // eslint-disable-line
  const put = async (body, what) => {
    setBusy(what);
    try {
      const r = await api(`/callcenter/${pid}/telegram`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.error || 'Saqlanmadi', { duration: 8000 }); return false; }
      setV(x => ({ ...d.channel, canManage: x ? x.canManage : true })); toast.success('Saqlandi');
      setTimeout(() => load(false), 2500);
      return true;
    } catch (e) { toast.error('Ulanib bo\'lmadi'); return false; } finally { setBusy(''); }
  };
  const regen = async () => {
    if (!window.confirm('Admin havolasi yangilansinmi? Eski havola ishlamay qoladi.')) return;
    try { const r = await post(`/callcenter/${pid}/telegram/code`, {}); const d = await r.json().catch(() => ({})); if (r.ok) setV(x => ({ ...x, ...d.channel })); else toast.error(d.error || 'Xato'); } catch (e) { }
  };
  const removeAdmin = async (a) => {
    if (!window.confirm(`${a.name || a.id} bot adminligidan chiqarilsinmi?`)) return;
    try { const r = await api(`/callcenter/${pid}/telegram/admins/${a.id}`, { method: 'DELETE' }); const d = await r.json().catch(() => ({})); if (r.ok) setV(x => ({ ...x, ...d.channel })); } catch (e) { }
  };
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast.success('Nusxa olindi'); } catch (e) { window.prompt('Nusxa oling:', t); } };

  if (!v) return <div className="text-sm text-gray-400">Yuklanmoqda…</div>;
  const [stCls, stLabel] = TG_STATUS[v.status] || TG_STATUS.off;
  const owner = !!v.canManage;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <div className={`${card} space-y-3`}>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-semibold text-gray-800 dark:text-gray-200">✈️ Telegram bot</p>
            <span className={`text-[11px] rounded-full px-2 py-0.5 ${stCls}`}>{stLabel}</span>
            {v.botLink && <a href={v.botLink} target="_blank" rel="noreferrer" className="text-sm text-primary-700 dark:text-primary-400 hover:underline" translate="no">@{v.botUsername}</a>}
            {v.openQuestions > 0 && <span className="text-[11px] bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 rounded-full px-2 py-0.5">❓ {v.openQuestions} javobsiz</span>}
          </div>
          {v.status === 'error' && v.lastError && <div className="text-xs text-red-600 dark:text-red-400">⚠️ {v.lastError}</div>}
          {v.lastUpdateAt && <div className="text-[11px] text-gray-400">Oxirgi xabar: {fmtTime(v.lastUpdateAt)}</div>}
          {!owner ? (
            <div className="text-xs text-gray-500 dark:text-gray-400">Bot sozlamasini faqat loyiha egasi o'zgartiradi.</div>
          ) : (
            <>
              <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{v.configured ? '🔑 Tokenni almashtirish (yangi bot)' : '🔑 Bot tokeni (@BotFather)'}</span>
                <div className="flex gap-2">
                  <input type="password" autoComplete="off" className={inp} placeholder="123456789:AAH…" value={token} onChange={(e) => setToken(e.target.value)} />
                  <button onClick={async () => { if (await put({ token }, 'token')) setToken(''); }} disabled={!token.trim() || busy === 'token'} className="px-4 rounded-lg bg-primary-700 hover:bg-primary-800 text-white text-sm whitespace-nowrap disabled:opacity-50">{busy === 'token' ? '⏳' : (v.configured ? 'Almashtirish' : 'Ulash')}</button>
                </div>
                <span className="text-[11px] text-gray-400">Token shifrlangan saqlanadi va qayta ko'rsatilmaydi.</span></label>
              {v.configured && (
                <>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={!!v.enabled} disabled={busy === 'en'} onChange={(e) => put({ enabled: e.target.checked }, 'en')} /> Bot yoqilgan (mijozlarga javob beradi)
                  </label>
                  <label className="block"><span className="text-xs font-semibold text-gray-500 dark:text-gray-400">/start salomi (bo'sh — 🤖 Agent salomi)</span>
                    <textarea rows={2} className={inp} value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Assalomu alaykum! Savolingizni yozing…" />
                  </label>
                  <button onClick={() => put({ greeting }, 'gr')} disabled={busy === 'gr'} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">Salomni saqlash</button>
                </>
              )}
            </>
          )}
        </div>
        {v.configured && (
          <div className={`${card} space-y-3`}>
            <p className="font-semibold text-gray-800 dark:text-gray-200">👥 Bot adminlari (mijoz-kompaniya xodimlari)</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Admin bot ichida savol-javob qo'shadi (/qoshish, /tahrir, .docx yuborish) va bot bilmagan savollarga javob beradi (xabarga reply) — javob mijozga ketadi va bazaga qo'shiladi.</p>
            {owner && v.adminLink && (
              <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 p-3 space-y-2">
                <div className="text-xs text-gray-600 dark:text-gray-300">Bir martalik admin havolasi — xodimga yuboring, u bosib «Start» qiladi:</div>
                <div className="flex gap-2 items-center">
                  <code className="text-xs bg-white dark:bg-gray-800 rounded px-2 py-1 flex-1 truncate" translate="no">{v.adminLink}</code>
                  <button onClick={() => copy(v.adminLink)} className="px-3 py-1 rounded-lg bg-primary-700 text-white text-xs whitespace-nowrap">📋 Nusxa</button>
                  <button onClick={regen} className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap" title="Eski havolani bekor qilish">🔄</button>
                </div>
                <div className="text-[11px] text-gray-400">Ishlatilgach havola o'zi almashadi (keyingi xodim uchun yangisini shu yerdan oling).</div>
              </div>
            )}
            {(v.admins || []).length === 0 ? <div className="text-sm text-gray-400">Hali admin yo'q — javobsiz savollar sizga (Khorezmai Telegram/push) keladi.</div> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {v.admins.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-2 text-sm">
                    <span className="flex-1 text-gray-800 dark:text-gray-200" translate="no">{a.name || a.id}{a.username ? ` (@${a.username})` : ''}</span>
                    {a.at && <span className="text-xs text-gray-400">{fmtTime(a.at)}</span>}
                    {owner && <button onClick={() => removeAdmin(a)} className="text-xs text-red-600 hover:underline">Chiqarish</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div className={`${card} text-xs text-gray-500 dark:text-gray-400 space-y-2`}>
        <p className="font-semibold text-gray-700 dark:text-gray-300 text-sm">Qanday ulanadi</p>
        <p>1. Telegram'da <b translate="no">@BotFather</b> → <code>/newbot</code> → nom va username (masalan, <span translate="no">kargo_yordam_bot</span>) → token beradi.</p>
        <p>2. Tokenni chapga qo'yib «Ulash». Bot darhol ishlaydi: mijoz yozadi (matn yoki ovoz) — agent 📚 Savol-javoblar va 🤖 Agent bilimlaridan javob beradi.</p>
        <p>3. Admin havolasini kompaniya xodimiga yuboring — u savollarni botning o'zidan qo'shadi va bot bilmagan savollarga javob beradi.</p>
        <p className="text-amber-700 dark:text-amber-400">⚠️ Mavjud botingiz (boshqa dastur ishlatayotgan) tokenini bermang — ikkalasi to'qnashadi. Alohida yangi bot yarating.</p>
        <p>Har suhbat 💬 Suhbatlarda (✈️ belgisi bilan), 30 daqiqa jimlikdan keyin AI tahlil qiladi. Narx: bitta javob ~$0.001–0.003.</p>
      </div>
    </div>
  );
}
